'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import { supabase } from '@/lib/supabase';

interface StateRegulation {
  id: string;
  state_code: string;
  state_name: string;
  data_availability: 'complete' | 'partial' | 'pending';
  last_updated: string;
}

interface RegulationDocument {
  id: string;
  title: string;
  category: string;
  status: string;
  effective_date: string | null;
  description: string;
  bill_number: string | null;
  document_url: string | null;
  pdf_url: string | null;
  last_modified: string | null;
}

interface RegulationUpdate {
  id: string;
  update_type: string;
  update_description: string;
  detected_date: string;
  source_url: string | null;
}

export default function StateSpecificRegulationsPage() {
  const [states, setStates] = useState<StateRegulation[]>([]);
  const [selectedState, setSelectedState] = useState<StateRegulation | null>(null);
  const [documents, setDocuments] = useState<RegulationDocument[]>([]);
  const [updates, setUpdates] = useState<RegulationUpdate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchDocuments(selectedState.id);
      fetchUpdates(selectedState.id);
    }
  }, [selectedState]);

  const fetchStates = async () => {
    try {
      const { data, error } = await supabase
        .from('state_regulations')
        .select('*')
        .order('state_name');

      if (error) throw error;
      setStates(data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (stateId: string) => {
    try {
      const { data, error } = await supabase
        .from('regulation_documents')
        .select('*')
        .eq('state_regulation_id', stateId)
        .order('last_modified', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchUpdates = async (stateId: string) => {
    try {
      const { data: docs } = await supabase
        .from('regulation_documents')
        .select('id')
        .eq('state_regulation_id', stateId);

      if (docs && docs.length > 0) {
        const docIds = docs.map(doc => doc.id);
        const { data, error } = await supabase
          .from('regulation_updates')
          .select('*')
          .in('regulation_document_id', docIds)
          .order('detected_date', { ascending: false })
          .limit(10);

        if (error) throw error;
        setUpdates(data || []);
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
    }
  };

  const getAvailabilityBadge = (availability: string) => {
    switch (availability) {
      case 'complete':
        return <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">Complete Documentation</span>;
      case 'partial':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">Partial Coverage</span>;
      case 'pending':
        return <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">Pending Updates</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">No Data</span>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'elevator_codes':
        return '📋';
      case 'safety_standards':
        return '🛡️';
      case 'inspection_requirements':
        return '🔍';
      case 'licensing_standards':
        return '📜';
      case 'building_codes':
        return '🏗️';
      default:
        return '📄';
    }
  };

  const getCategoryLabel = (category: string) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredStates = states?.filter(state => 
    state?.state_name?.toLowerCase()?.includes(searchQuery?.toLowerCase() || '')
  );

  const filteredDocuments = documents?.filter(doc => {
    const matchesCategory = categoryFilter === 'all' || doc?.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || doc?.status === statusFilter;
    return matchesCategory && matchesStatus;
  });

  const groupedDocuments = filteredDocuments?.reduce((acc, doc) => {
    const category = doc.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(doc);
    return acc;
  }, {} as Record<string, RegulationDocument[]>);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              State Specific Regulations
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto">
              Comprehensive database of state-by-state elevator codes, safety standards, 
              and legislative updates. Stay informed about compliance requirements and proposed changes across all 50 states.
            </p>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-6 mb-8 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm text-gray-700">Complete Documentation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span className="text-sm text-gray-700">Partial Coverage</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-400 rounded"></div>
              <span className="text-sm text-gray-700">Pending Updates</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* State List Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Select State</h2>
                
                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search states..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>

                {/* State List */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    </div>
                  ) : (
                    filteredStates?.map((state) => (
                      <button
                        key={state.id}
                        onClick={() => setSelectedState(state)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                          selectedState?.id === state.id
                            ? 'bg-gray-900 text-white shadow-lg'
                            : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold">{state.state_name}</div>
                            <div className="text-xs mt-1 opacity-75">{state.state_code}</div>
                          </div>
                          <div className={`w-3 h-3 rounded-full ${
                            state.data_availability === 'complete' ? 'bg-green-500' :
                            state.data_availability === 'partial'? 'bg-yellow-500' : 'bg-gray-400'
                          }`}></div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Regulation Details */}
            <div className="lg:col-span-2">
              {!selectedState ? (
                <div className="bg-gray-50 rounded-lg p-12 text-center">
                  <div className="text-6xl mb-4">🗺️</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Select a State</h3>
                  <p className="text-gray-600">Choose a state from the list to view detailed elevator regulations, codes, and compliance requirements.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* State Header */}
                  <div className="bg-gradient-to-r from-gray-900 to-gray-700 rounded-lg p-8 text-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-3xl font-bold mb-2">{selectedState.state_name}</h2>
                        <p className="text-gray-300 mb-4">State Code: {selectedState.state_code}</p>
                        {getAvailabilityBadge(selectedState.data_availability)}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-300">Last Updated</div>
                        <div className="text-lg font-semibold">{formatDate(selectedState.last_updated)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                        <select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        >
                          <option value="all">All Categories</option>
                          <option value="elevator_codes">Elevator Codes</option>
                          <option value="safety_standards">Safety Standards</option>
                          <option value="inspection_requirements">Inspection Requirements</option>
                          <option value="licensing_standards">Licensing Standards</option>
                          <option value="building_codes">Building Codes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="proposed">Proposed</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Recent Updates */}
                  {updates && updates.length > 0 && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
                      <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                        <span>🔔</span> Recent Legislative Updates
                      </h3>
                      <div className="space-y-3">
                        {updates.slice(0, 3).map((update) => (
                          <div key={update.id} className="bg-white rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                                {update.update_type}
                              </span>
                              <span className="text-xs text-gray-500">{formatDate(update.detected_date)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{update.update_description}</p>
                            {update.source_url && (
                              <a
                                href={update.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 mt-2 inline-block"
                              >
                                View Source →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Regulation Documents by Category */}
                  {documents && documents.length > 0 ? (
                    <div className="space-y-6">
                      {Object.entries(groupedDocuments || {}).map(([category, docs]) => (
                        <div key={category} className="bg-white rounded-lg shadow-lg overflow-hidden">
                          <div className="bg-gray-900 text-white px-6 py-4">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                              <span>{getCategoryIcon(category)}</span>
                              {getCategoryLabel(category)}
                              <span className="ml-auto text-sm font-normal bg-gray-700 px-3 py-1 rounded-full">
                                {docs.length} {docs.length === 1 ? 'Document' : 'Documents'}
                              </span>
                            </h3>
                          </div>
                          <div className="divide-y divide-gray-200">
                            {docs.map((doc) => (
                              <div key={doc.id} className="p-6 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">{doc.title}</h4>
                                    {doc.bill_number && (
                                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded mb-2">
                                        {doc.bill_number}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                    doc.status === 'active' ? 'bg-green-100 text-green-800' :
                                    doc.status === 'proposed'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {doc.status?.toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-gray-700 mb-4 leading-relaxed">{doc.description}</p>
                                <div className="flex items-center gap-6 text-sm text-gray-600">
                                  {doc.effective_date && (
                                    <div className="flex items-center gap-1">
                                      <span className="font-semibold">Effective:</span>
                                      <span>{formatDate(doc.effective_date)}</span>
                                    </div>
                                  )}
                                  {doc.last_modified && (
                                    <div className="flex items-center gap-1">
                                      <span className="font-semibold">Modified:</span>
                                      <span>{formatDate(doc.last_modified)}</span>
                                    </div>
                                  )}
                                </div>
                                {(doc.document_url || doc.pdf_url) && (
                                  <div className="mt-4 flex gap-3">
                                    {doc.document_url && (
                                      <a
                                        href={doc.document_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                                      >
                                        View Online →
                                      </a>
                                    )}
                                    {doc.pdf_url && (
                                      <a
                                        href={doc.pdf_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                                      >
                                        Download PDF ↓
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-12 text-center">
                      <div className="text-6xl mb-4">📋</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">No Regulations Available</h3>
                      <p className="text-gray-600">Regulation data for {selectedState.state_name} is currently being compiled. Check back soon for updates.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Need Compliance Assistance?</h2>
          <p className="text-lg text-gray-300 mb-8">
            Our team stays current with all state-specific regulations and can help ensure your elevator projects meet compliance requirements.
          </p>
          <Link
            href="/contact"
            className="inline-block bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Contact Our Experts
          </Link>
        </div>
      </section>
    </div>
  );
}